import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calendar as CalendarIcon, Clock, MapPin, Users, Info } from 'lucide-react';
import { venueGamesService } from '@/services/venueGamesService';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';
import PermissionGate from '@/components/rbac/PermissionGate';

interface VenueEventSubmissionFormProps {
  venueId: string;
  onSuccess?: () => void;
}

interface EventFormData {
  event_title: string;
  event_description: string;
  event_date: string;
  event_time: string;
  event_location: string;
  max_participants?: number;
  registration_required: boolean;
}

export const VenueEventSubmissionForm = ({ venueId, onSuccess }: VenueEventSubmissionFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const user = supabaseUser || internalUser;

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<EventFormData>({
    defaultValues: {
      registration_required: false
    }
  });

  const registrationRequired = watch('registration_required');

  const onSubmit = async (data: EventFormData) => {
    if (!user?.id) {
      toast.error('Please log in to submit an event');
      return;
    }

    setIsSubmitting(true);
    try {
      await venueGamesService.submitEvent({
        venue_id: venueId,
        posted_by: user.id,
        posted_by_role: user.role || 'coach',
        current_participants: 0,
        ...data
      });

      toast.success('Event submitted successfully! Waiting for venue approval.');
      reset();
      onSuccess?.();
    } catch (error) {
      console.error('Error submitting event:', error);
      toast.error('Failed to submit event');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800">
        <CardContent className="p-6">
          <div className="text-center text-gray-400">
            Please log in to submit an event
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <PermissionGate 
      permission="create_events"
      fallback={
        <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-white flex items-center gap-2">
              <CalendarIcon className="w-6 h-6 text-primary" />
              Submit Event Request
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <Alert className="border-orange-500/50 bg-orange-500/10">
              <Info className="h-4 w-4 text-orange-400" />
              <AlertDescription className="text-orange-300">
                <strong>Event Creation Restricted:</strong> Only Team Owners, Coaches, and Venues can create events. 
                This ensures proper oversight and safety protocols. If you need an event created, please contact 
                an authorized user in your area.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      }
    >
      <Card className="bg-gradient-to-br from-gray-900 to-black border-gray-800">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-white flex items-center gap-2">
            <CalendarIcon className="w-6 h-6 text-primary" />
            Submit Event Request
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <Alert className="mb-6 border-blue-500/50 bg-blue-500/10">
            <Info className="h-4 w-4 text-blue-400" />
            <AlertDescription className="text-blue-300">
              <strong>Who can create events:</strong> Team Owners, Coaches, and Venues only. 
              This ensures proper safety oversight and event management.
            </AlertDescription>
          </Alert>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="event_title" className="text-white">Event Title *</Label>
              <Input
                id="event_title"
                {...register('event_title', { required: 'Event title is required' })}
                className="bg-gray-800 border-gray-700 text-white"
                placeholder="e.g., Youth Basketball Camp"
              />
              {errors.event_title && (
                <p className="text-red-400 text-sm">{errors.event_title.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="event_location" className="text-white">Event Location</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary w-4 h-4" />
                <Input
                  id="event_location"
                  {...register('event_location')}
                  className="bg-gray-800 border-gray-700 text-white pl-10"
                  placeholder="Main Hall, Outdoor Courts, etc."
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="event_date" className="text-white">Event Date *</Label>
              <Input
                id="event_date"
                type="date"
                {...register('event_date', { required: 'Event date is required' })}
                className="bg-gray-800 border-gray-700 text-white"
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.event_date && (
                <p className="text-red-400 text-sm">{errors.event_date.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="event_time" className="text-white">Event Time *</Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary w-4 h-4" />
                <Input
                  id="event_time"
                  type="time"
                  {...register('event_time', { required: 'Event time is required' })}
                  className="bg-gray-800 border-gray-700 text-white pl-10"
                />
              </div>
              {errors.event_time && (
                <p className="text-red-400 text-sm">{errors.event_time.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="event_description" className="text-white">Event Description</Label>
            <Textarea
              id="event_description"
              {...register('event_description')}
              className="bg-gray-800 border-gray-700 text-white"
              placeholder="Describe your event, activities, requirements, etc..."
              rows={4}
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="text-white">Registration Required</Label>
                <p className="text-sm text-gray-400">
                  Do participants need to register in advance?
                </p>
              </div>
              <Switch
                checked={registrationRequired}
                onCheckedChange={(checked) => setValue('registration_required', checked)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="max_participants" className="text-white">
                Maximum Participants
              </Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary w-4 h-4" />
                <Input
                  id="max_participants"
                  type="number"
                  {...register('max_participants', { 
                    valueAsNumber: true,
                    min: { value: 1, message: 'Must be at least 1 participant' }
                  })}
                  className="bg-gray-800 border-gray-700 text-white pl-10"
                  placeholder="Leave empty for unlimited"
                  min="1"
                />
              </div>
              <p className="text-sm text-gray-400">
                Leave empty if there's no participant limit
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-sports-green text-black hover:bg-sports-green/90 font-bold"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Event Request'}
            </Button>
          </div>
          </form>
        </CardContent>
      </Card>
    </PermissionGate>
  );
};